import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useLogoutMutation } from '@/store/api';
import { useAppDispatch, useAppSelector } from '@/store';
import { signedOut, } from '@/store/authSlice';
import { toggleNav } from '@/store/uiSlice';
import { useLiveUpdates } from '@/hooks/useLiveUpdates';
import { Toasts } from './Toasts';

/**
 * Navigation is grouped by what an operator is doing, not by data model:
 * what happened on the phone, what the phone is allowed to say, and account.
 *
 * Every screen below is organization-scoped. A platform admin has no
 * organization, so showing these to them would be showing a menu of links that
 * all return 403.
 */
const RESTAURANT_NAV_GROUPS = [
  {
    heading: 'Activity',
    items: [
      { to: '/', label: 'Overview', end: true },
      { to: '/calls', label: 'Calls' },
      { to: '/orders', label: 'Orders' }
    ]
  },
  {
    heading: 'What it says',
    items: [
      { to: '/menu', label: 'Menu' },
      { to: '/business', label: 'Business info' },
      { to: '/answers', label: 'Answers' },
      { to: '/voice', label: 'Voice' },
      { to: '/test', label: 'Test call' },
      { to: '/pronunciation', label: 'Test pronunciation' },
      { to: '/hours', label: 'Hours' }
    ]
  },
  {
    heading: 'Account',
    items: [
      { to: '/phone', label: 'Phone numbers' },
      { to: '/integrations', label: 'Point of sale' },
      { to: '/payments', label: 'Payments' },
      { to: '/team', label: 'Team' },
      { to: '/billing', label: 'Plan and usage' }
    ]
  }
];

/** Platform owner screens; the API refuses them to everyone else. */
const PLATFORM_NAV = [
  { to: '/admin', label: 'Admin' },
  { to: '/admin/telnyx', label: 'Telnyx' }
];

export function AppShell() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [logout] = useLogoutMutation();
  const { user, role, organizationId } = useAppSelector((state) => state.auth);
  const navOpen = useAppSelector((state) => state.ui.navOpen);

  const isPlatformAdmin = role === 'SUPER_ADMIN' || Boolean(user?.isPlatformAdmin);
  // Impersonation gives a platform admin a real organization, and with it the
  // operator screens. Without one there is nothing org-scoped to show.
  const navGroups = organizationId ? RESTAURANT_NAV_GROUPS : [];
  // Calls, orders and the overview update as calls happen, no refresh.
  useLiveUpdates(Boolean(organizationId));

  const signOut = async () => {
    await logout().unwrap().catch(() => undefined);
    dispatch(signedOut());
    navigate('/login');
  };

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[232px_1fr]">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:rounded focus:bg-white focus:px-3 focus:py-2"
      >
        Skip to content
      </a>

      <aside
        className={`${
          navOpen ? 'block' : 'hidden'
        } bg-ink px-3 py-5 lg:sticky lg:top-0 lg:block lg:h-screen lg:overflow-y-auto`}
      >
        <div className="px-2 pb-6">
          <p className="text-base font-semibold text-white">CallWaiter</p>
          <p className="mt-0.5 text-micro text-white/50">Answers the phone, takes the order</p>
        </div>

        <nav className="space-y-6">
          {navGroups.map((group) => (
            <div key={group.heading}>
              <p className="px-2 pb-1.5 text-micro text-white/40">{group.heading}</p>
              <ul className="space-y-0.5">
                {group.items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.end}
                      onClick={() => dispatch(toggleNav(false))}
                      className={({ isActive }) =>
                        `block rounded px-2 py-1.5 text-small transition-colors ${
                          isActive
                            ? 'bg-ink-line text-white'
                            : 'text-white/70 hover:bg-ink-soft hover:text-white'
                        }`
                      }
                    >
                      {item.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {isPlatformAdmin ? (
            <div>
              <p className="px-2 pb-1.5 text-micro text-white/40">Platform</p>
              <ul className="space-y-0.5">
                {PLATFORM_NAV.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end
                      onClick={() => dispatch(toggleNav(false))}
                      className={({ isActive }) =>
                        `block rounded px-2 py-1.5 text-small transition-colors ${
                          isActive
                            ? 'bg-ink-line text-white'
                            : 'text-white/70 hover:bg-ink-soft hover:text-white'
                        }`
                      }
                    >
                      {item.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </nav>
      </aside>

      <div className="flex min-h-screen flex-col">
        <header className="sticky top-0 z-10 flex items-center gap-3 border-b border-rule bg-paper/95 px-4 py-3 backdrop-blur">
          <button
            className="rounded border border-rule px-2 py-1 text-small lg:hidden"
            onClick={() => dispatch(toggleNav())}
            aria-expanded={navOpen}
          >
            Menu
          </button>

          <div className="ml-auto flex items-center gap-3">
            {isPlatformAdmin && organizationId ? (
              <span className="rounded bg-rust-soft px-2 py-0.5 text-micro font-medium text-rust">
                Viewing a customer account
              </span>
            ) : null}
            <span className="hidden text-small text-slate sm:inline">{user?.email}</span>
            <button onClick={signOut} className="text-small text-slate hover:text-ink">
              Sign out
            </button>
          </div>
        </header>

        <main id="main" className="mx-auto w-full max-w-6xl flex-1 px-4 py-6">
          <Outlet />
        </main>
      </div>

      <Toasts />
    </div>
  );
}
